import type { ComboboxOption } from '@sparklayer-io/spark-dash-ui/components/ui/combobox/types';
import { Card, Checkbox, Combobox, Input, Text } from '@ui/themed';
import { t } from 'i18next';
import { withFieldGroup } from '../../../lib/app-form';
import { fieldErrorMessage } from '../../../utils/form';
import { useSectionContextOptions } from '../hooks/use-section-context-options';
import type { SectionContextValues } from '../section-context-schema';

/** i18n key prefix for a form's `.context` sub-tree (company or section). */
export type SectionContextPrefix =
  | 'features:companies.pages.company_form_page.context'
  | 'features:sections.pages.section_form_page.context';

/** The shared "context" fields (a subset of the section context) this group binds. */
const SECTION_CONTEXT_DEFAULTS: Omit<
  SectionContextValues,
  'name' | 'external_id' | 'integration_id' | 'accounting_id'
> = {
  customer_group: '',
  price_lists: [],
  sales_agent_groups: [],
  payment_on_account_enabled: false,
  payment_on_account: { credit_limit: '', balance: '', currency_code: '', net_terms: '' },
};

/** Ensure already-selected values always appear as options (even if not in the fetched list). */
function withSelected(options: ComboboxOption[], selected: string[]): ComboboxOption[] {
  const known = new Set(options.map((option) => option.value));
  const extra = selected
    .filter((value) => !known.has(value))
    .map((value) => ({ value, label: value }));
  return [...options, ...extra];
}

/**
 * The "context" card shared by the company and section forms: customer group,
 * price lists, sales-agent groups and payment-on-account. Field labels are
 * shared (they live under the company namespace); only the card title,
 * description and the checkbox id differ per form.
 */
export const ContextFieldsCard = withFieldGroup({
  defaultValues: SECTION_CONTEXT_DEFAULTS,
  props: {} as { contextPrefix: SectionContextPrefix },
  render: function ContextFieldsCardRender({ group, contextPrefix }) {
    const { customerGroupOptions, priceListOptions, salesAgentGroupOptions, netTermsOptions } =
      useSectionContextOptions();

    return (
      <Card>
        <Card.Header headingLevel="h2">{t(`${contextPrefix}.title`)}</Card.Header>
        <Card.Body>
          <div className="flex flex-col gap-4">
            <Text variant="secondary">{t(`${contextPrefix}.description`)}</Text>
            <div className="grid w-full gap-3 sm:grid-cols-2">
              <group.Field name="customer_group">
                {(field) => (
                  <Combobox
                    clearable
                    label={t(
                      'features:companies.pages.company_form_page.context.customer_group_label',
                    )}
                    placeholder={t(
                      'features:companies.pages.company_form_page.context.customer_group_placeholder',
                    )}
                    options={withSelected(
                      customerGroupOptions,
                      field.state.value ? [field.state.value] : [],
                    )}
                    value={field.state.value || null}
                    onChange={(value) => field.handleChange(value ?? '')}
                  />
                )}
              </group.Field>
              <group.Field name="price_lists">
                {(field) => (
                  <Combobox
                    multiple
                    label={t(
                      'features:companies.pages.company_form_page.context.price_lists_label',
                    )}
                    placeholder={t(
                      'features:companies.pages.company_form_page.context.price_lists_placeholder',
                    )}
                    options={withSelected(priceListOptions, field.state.value)}
                    value={field.state.value}
                    onChange={(value) => field.handleChange(value)}
                  />
                )}
              </group.Field>
              <group.Field name="sales_agent_groups">
                {(field) => (
                  <Combobox
                    multiple
                    label={t(
                      'features:companies.pages.company_form_page.context.sales_agent_groups_label',
                    )}
                    placeholder={t(
                      'features:companies.pages.company_form_page.context.sales_agent_groups_placeholder',
                    )}
                    options={withSelected(salesAgentGroupOptions, field.state.value)}
                    value={field.state.value}
                    onChange={(value) => field.handleChange(value)}
                  />
                )}
              </group.Field>
            </div>

            <group.Field name="payment_on_account_enabled">
              {(field) => (
                <Checkbox
                  label={t(
                    'features:companies.pages.company_form_page.context.payment_on_account.enable_label',
                  )}
                  checked={field.state.value}
                  onChange={() => {
                    const enabled = !field.state.value;
                    field.handleChange(enabled);
                    // Clear hidden fields so stale invalid values (e.g. a bad currency
                    // code) can't block submit with no visible error to correct.
                    if (!enabled) {
                      group.setFieldValue('payment_on_account', {
                        credit_limit: '',
                        balance: '',
                        currency_code: '',
                        net_terms: '',
                      });
                    }
                  }}
                />
              )}
            </group.Field>

            <group.Subscribe selector={(state) => state.values.payment_on_account_enabled}>
              {(enabled) =>
                enabled ? (
                  <div className="grid w-full gap-3 sm:grid-cols-2">
                    <group.Field name="payment_on_account.balance">
                      {(field) => (
                        <Input
                          label={t(
                            'features:companies.pages.company_form_page.context.payment_on_account.balance_label',
                          )}
                          inputMode="decimal"
                          value={field.state.value}
                          onChange={field.handleChange}
                          error={fieldErrorMessage(field.state.meta.errors)}
                        />
                      )}
                    </group.Field>
                    <group.Field name="payment_on_account.credit_limit">
                      {(field) => (
                        <Input
                          label={t(
                            'features:companies.pages.company_form_page.context.payment_on_account.credit_limit_label',
                          )}
                          inputMode="decimal"
                          value={field.state.value}
                          onChange={field.handleChange}
                          error={fieldErrorMessage(field.state.meta.errors)}
                        />
                      )}
                    </group.Field>
                    <group.Field name="payment_on_account.currency_code">
                      {(field) => (
                        <Input
                          label={t(
                            'features:companies.pages.company_form_page.context.payment_on_account.currency_label',
                          )}
                          placeholder={t(
                            'features:companies.pages.company_form_page.context.payment_on_account.currency_placeholder',
                          )}
                          maxLength={3}
                          value={field.state.value}
                          onChange={field.handleChange}
                          error={fieldErrorMessage(field.state.meta.errors)}
                        />
                      )}
                    </group.Field>
                    <group.Field name="payment_on_account.net_terms">
                      {(field) => (
                        <Combobox
                          clearable
                          label={t(
                            'features:companies.pages.company_form_page.context.payment_on_account.net_terms_label',
                          )}
                          placeholder={t(
                            'features:companies.pages.company_form_page.context.payment_on_account.net_terms_placeholder',
                          )}
                          options={netTermsOptions}
                          value={field.state.value || null}
                          onChange={(value) =>
                            field.handleChange((value ?? '') as typeof field.state.value)
                          }
                        />
                      )}
                    </group.Field>
                  </div>
                ) : null
              }
            </group.Subscribe>
          </div>
        </Card.Body>
      </Card>
    );
  },
});
